import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal, Signal, Inject, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, Observable, of as observableOf, interval, throwError } from 'rxjs';
import { map, tap, catchError, shareReplay, finalize } from 'rxjs/operators';

import { JsonPatchOperation } from '../interfaces/json-patch-operation';
import Word from '../models-internal/word';
import AppSetting from '../models/app-setting';
import ApplicationSettings from '../models/application-settings';
import PagedResults from '../models/paged-results';
import AppSettings from '../models/app-settings';
import AppSettingSearchRequest from '../models/app-setting-search-request';
import SubscriptionPlanConfiguration from '../models/subscription-plan-configuration';
import { UserGeoLocation } from '../models/user-geo-location';

import { AppStorageService } from './app-storage.service';
import { HttpHelperService } from './http-helper-service.service';
import { LogService } from './log.service';
import { SHARED_CORE_CONFIG, SharedCoreConfig } from '../config/shared-core-config';

@Injectable({
  providedIn: 'root'
})
export class AppSettingsService {
  private readonly _componentName = 'AppSettingsService';

  // The interval size in milliseconds (30000 == 30 seconds)
  static readonly APP_FLAG_REFRESH_INTERVAL = 30000;

  applicationSettingFlags: ApplicationSettings;

  /**
   * The default Customer Support phone number for Outdoor Access
   */
  public supportPhoneNumber = '804-404-8664';

  /**
  Same value as variables.less: @desktop-mobile-division-width (I couldn't figure out a way to read-in the variables.less via Typescript)
  
  The width to use in media queries for the mobile-desktop width division.
  Set to 767px (so that 768 is the first width to be included in desktop media queries)
 */
  public desktopMobileDivisionPixelWidth = 767;

  /**
   * Extra large devices (large laptops and desktops, 1200px and up)
   */
  public desktopWidePixelWidth = 1200;

  /**
   * Subject for enabling/disabling Ambassador Home page on the OA Platform.
   */
  public ambassadorHomeEnabled = new BehaviorSubject<boolean>(false);

  /**
   * Gets or sets the environment code (P = Prod, S = Staging,  B=Beta,  D = Development, U = Unknown/Unspecified)
   */
  public environmentCode = new BehaviorSubject<string>('');

  /**
   * Gets or sets the maximum number of characters for the HTML Editor Component
   */
  public htmlEditorMaximumCharacters = new BehaviorSubject<number>(4000);

  /**
   * Compile time data from when the Angular application was compiled: UTC Timestamp and GIT Commit Hash
   */
  private compileTimeData: any;

  /**
   * The ISO String of the UTC Application Vintage as reported by the API
   */
  public get applicationVintage(): string {
    return this.storage.getSessionItem('applicationVintage');
  }

  /**
   * The ISO String of the UTC Application Vintage as reported by the API
   */
  public set applicationVintage(v: string) {
    this.storage.setSessionItem('applicationVintage', v);
  }

  constructor(
    private http: HttpClient,
    private httpHelper: HttpHelperService,
    private route: ActivatedRoute,
    private log: LogService,
    private storage: AppStorageService,
    @Optional() @Inject(SHARED_CORE_CONFIG) private sharedCoreConfig?: SharedCoreConfig
  ) {
    this.refreshFlagSettings();
    this.initializeSettings();
  }

    /**
     * Is verbose logging enabled
     */
    private get verboseLogging(): boolean {      
      return this.log.verboseLogging;
    }

  
  /**
   * Calls the getFlagSettings function and sets the location 'applicationSettingFlags' field
   */
  private refreshFlagSettings() {
    this.getFlagSettings().then((v) => {
      this.applicationSettingFlags = v;
      if (this.verboseLogging) {
        console.log('applicationSettingFlags set to ', this.applicationSettingFlags);
      }
    });
  }

  /**
   * forceClientReload
   */
  public forceClientReload() {
    return this.http.get('system/forceReload');
  }

  private settings: AppSettings;
  private inFlight$: Observable<Readonly<AppSettings>> | null = null;
  private _settingsSignal = signal<AppSettings | null>(null);
  public settingsSignal: Signal<AppSettings | null> = this._settingsSignal;
  /**
   * Observable holding the latest AppSettings (or null until loaded).
   * Callers can subscribe to `settings$` instead of calling `load().subscribe()`.
   */
  public settings$ = new BehaviorSubject<Readonly<AppSettings> | null>(null);

  /**
   * Get's the default OA Brokerage Percent
   */
  get defaultOABrokerage() {
    return this.settings && this.settings.defaultOABrokerage;
  }

  get apiBaseUrl() {
    return this.sharedCoreConfig?.apiUrl || '';
  }

  get storeUrl() {
    // 
    return this.settings && this.settings.storeUrl;
  }

  get froalaLicenseKey() {
    // 
    return this.settings && this.settings.froalaLicenseKey;
  }

  get extendedDiscountTitle() {
    // 
    return this.settings && this.settings.extendedDiscountTitle;
  }

  get versionIndicator() {
    return this.settings && this.settings.versionIndicator;
  }
  get enableReservations() {
    return this.settings && this.settings.enableReservations;
  }

  /**
   * A setting that controls what the default annual lease fee amount will be when
   * creating a brand new annual lease
   */
  get defaultAnnualLeaseFeeAmount() {
    return (this.settings && this.settings.defaultAnnualLeaseFeeAmount) || 150;
  }


  

  get leaseMembershipFeeDescription() {
    // 
    return this.settings && this.settings.leaseMembershipFeeDescription;
  }

  // no more membershipTrialPeriodDays since this setting was not plan-specific

  get membershipDefaultDiscountCode() {
    return this.settings && this.settings.membershipDefaultDiscountCode;
  }

  get googleMapsKey() {
    return this.settings && this.settings.googleMapsKey;
  }

  get stripePublicKey() {
    return this.settings && this.settings.stripePublicKey;
  }

  get outdoorAccessPhoneNumber() {
    return this.settings && this.settings.outdoorAccessContactPhoneNumber;
  }

  public get maxAvailableDate() {
    return this.settings && this.settings.maxAvailableDate;
  }

  

  get facebookAppId() {
    return this.settings && this.settings.facebookAppId;
  }

  get homePageCampaignRotationSeconds() {
    return this.settings && this.settings.homePageCampaignRotationSeconds;
  }

  get allIconStyle() {
    return this.settings && this.settings.allIconStyle;
  }
  /**
   * Gets the base URL ( window.location.origin )
   */
  get baseUrl() {
    return window.location.origin;
  }

  /**
   * Gets the Maximum Credit Amount that an administrator can add to a user
   */
  public get maximumCreditAdditionAmount() {
    return this.settings && this.settings.maximumCreditAdditionAmount;
  }

  /**
   * The system BookingType Display Configuration
   */
  get bookingTypeDisplayConfiguration() {
    return this.settings && this.settings.bookingTypeDisplayConfiguration;
  }

  get searchMapClusterMaxZoom() {
    return this.settings && this.settings.searchMapClusterMaxZoom;
  }

  get mobileSearchMapClusterMaxZoom() {
    return this.settings && this.settings.mobileSearchMapClusterMaxZoom;
  }

  /**
   * Gets the initialQueryParams value that is set when the default.html is loaded
   */
  get initialQueryParams() {
    var json = this.storage.getSessionItem('initialQueryParams');

    if (json) {
      const o = JSON.parse(json);

      if (this.verboseLogging) {
        console.log(`${this._componentName}.initialQueryParams=`, o);
      }

      return o;
    }
    return {};
  }

  /**
   * Gets the subscription plan configuration (which includes the trial days for each subscription plan type)
   */
  get subscriptionPlanConfiguration() {
    const json = this.settings && this.settings.subscriptionPlanConfiguration;

    if (json) {
      let config: SubscriptionPlanConfiguration[] = JSON.parse(json);
      return config;
    }

    return [] as SubscriptionPlanConfiguration[];
  }

  /**
   * The Base URL for the Listing Attachment Images and Thumbnails
   */
  get attachmentBaseUrl() {
    // return "https://cdn.outdooraccess.com/oa-img"
    return this.settings && this.settings.attachmentBaseUrl;
  }

  /**
   * Base URL to the Azure Asset Container.  Use in place of placing large (quantity or size) assets in the Angular asset directory.
   */
  get assetContainerUrl() {
    // return "https://cdn.outdooraccess.com/oa-assets"
    return this.settings && this.settings.assetContainerUrl;
  }

  /**
   * A flag that controls how the source (and the resulting url) for listing attachments (photos and thumbnails)
   */
  get azureAttachmentContainerEnabled() {
    return this.settings && this.settings.azureAttachmentContainerEnabled;
  }

  public load(): Observable<Readonly<AppSettings>> {
    if (this.settings) {
      // ensure cached value is emitted for subscribers
      this.settings$.next(this.settings as Readonly<AppSettings>);
      this._settingsSignal.set(this.settings);
      return observableOf(this.settings as Readonly<AppSettings>);
    }

    // If a request is already in-flight, return the cached observable
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.http.get<AppSettings>('AppSettings').pipe(
      tap((res) => {
        this.environmentCode.next(res.environmentCode);
        this.ambassadorHomeEnabled.next(res.ambassadorHomeEnabled);

        this.settings = Object.freeze(res);
        // keep both Observable and Signal in sync for consumers
        this.settings$.next(this.settings as Readonly<AppSettings>);
        this._settingsSignal.set(this.settings);
      }),
      map(() => this.settings as Readonly<AppSettings>),
      shareReplay(1),
      catchError((err) => {
        console.error(`${this._componentName}.load() error`, err);
        return throwError(err);
      }),
      finalize(() => {
        this.inFlight$ = null;
      })
    );

    return this.inFlight$;
  }

  /**
   * Kick off an initial load so consumers can simply subscribe to `settings$`.
   */
  private initializeSettings() {
    this.load().subscribe({
      next: (s) => {
        this.settings$.next(s);
        this._settingsSignal.set(s as AppSettings);
      },
      error: (err) => {
        console.error(`${this._componentName}.initializeSettings() error`, err);
      },
    });
  }

  /**
   * Retrieves app settings from the API.
   */
  public refreshAppSettings() {
    this.http
      .get<AppSettings>('AppSettings')
      .pipe(
        tap((s) => {
          this.settings = Object.freeze(s);
          this.settings$.next(this.settings as Readonly<AppSettings>);
          this._settingsSignal.set(this.settings);
        }),
        catchError((err) => {
          console.error(`${this._componentName}.refreshAppSettings() error`, err);
          return observableOf(null as any);
        })
      )
      .subscribe();
  }

  /**
   * Gets an observable AppSettings
   */
  public getAppSettings(): Observable<Readonly<AppSettings>> {
    return this.load();
  }

  /**
   * Setting to indicate if all Tracked Events should be enabled (meaning that they are POSTed back to the API)
   * Works as a kill switch
   */
  public get enableTrackedEvents(): boolean {
    return this.applicationSettingFlags.enableTrackedEvents;
  }

  /**
   * Gets Application Setting Flags from the API
   */
  public async getFlagSettings() {
    var applicationSettings = {} as ApplicationSettings;

    let httpHeaders = new HttpHeaders();
    httpHeaders.append('responseType', 'arraybuffer');

    if (this.verboseLogging) {
      console.log('getFlagSettings before get');
    }

    const options = { responseType: 'arraybuffer' as 'json' };

    let buffer = await this.http.get<ArrayBuffer>('settings/app/flags', options).toPromise();

    if (this.verboseLogging) {
      console.log('getFlagSettings buffer', buffer);
    }

    let position: number = 0;
    applicationSettings.enableTrackedEvents = buffer[position] === 0 ? false : true;

    if (this.verboseLogging) {
      console.log('loadFlagSettings applicationSettings', applicationSettings);
    }

    return applicationSettings;
  }

  /**
   * Gets the raw application setting data
   */
  public getRawApplicationSettings() {
    return this.http.get<AppSettings[]>('appsettings/data');
  }

  /**
   * Checks the Application Vintage value from the server.
   */
  private checkApplicationVintage() {
    this.http.get<string>('appsettings/ngvintage').subscribe(
      (result) => {
        if (this.verboseLogging) {
          console.log('ngvintage', result);
        }

        if (this.applicationVintage) {
          if (this.applicationVintage === result) {
            if (this.verboseLogging) {
              console.log('No Change for applicationVintage property. No event emitted.', result);
            }
          } else {
            if (this.verboseLogging) {
              console.log(`ApplicationVintage values are different.  Current: ${this.applicationVintage} New: ${result}.`);
            }
            this.onApplicationVintageChange(result);
          }
        } else {
          if (this.verboseLogging) {
            console.log('The applicationVintage property does not have a value. No event emitted.', result);
          }
          this.applicationVintage = result;
        }
      },
      (err) => {
        console.error(err);
      }
    );
  }

  /**
   * Handles when the application vintage value changes
   * @param newValue
   */
  private onApplicationVintageChange(newValue: string) {
    console.log(`${this._componentName}.onApplicationVintageChange(${newValue}).`);

    // For now, we just force a reload of the Angular application if the value is different.
    // But in the future, we may wish to do something more intelligent here (like warn the user instead of just forcing the reload)
    this.applicationVintage = newValue;

    // let trackedEvent:TrackedEvent = {} as TrackedEvent;

    // let jsonData = {"action":"Force Browser Page Reload"};

    // trackedEvent.tierName = "Presentation";
    // trackedEvent.component = this._serviceName;
    // trackedEvent.category = "Trigger";
    // trackedEvent.jsonData = JSON.stringify(jsonData);

    // this.log.addTrackedEvent(trackedEvent).subscribe();

    console.info(`${this._componentName}.onApplicationVintageChange(${newValue}). Reloading OA Application...`);
    //location.reload();
  }

 


  /**
   * Patches a single value for an application setting
   * @param id
   * @param path
   * @param value
   */
  public patchValue(path: string, value: any) {
    let patchOperation = { path, value, op: 'replace' } as JsonPatchOperation;

    return this.httpHelper.patch('appsettings', [patchOperation]);
  }

  /**
   * Searches application settings
   * @param request
   */
  public search(request: AppSettingSearchRequest) {
    return this.http.post<PagedResults>('appsettings', request);
  }

  /**
   * Gets a single map setting
   */
  public getAppSetting(name: string) {
    return this.http.get<AppSetting>(`appsettings/${name}`);
  }

  public latLongToLocation(latitude: number, longitude: number) {
    return this.http.get<UserGeoLocation>(`locality/latlongtolocation?latitude=${latitude}&longitude=${longitude}`);
  }

  public ipAddressToLocation() {
    return this.http.get<UserGeoLocation>(`locality/ipaddrtolocation`);
  }

  /**
   * Gets the API URL for the OutdoorAccess Piebald API (next-gen API)
   * https://outdooraccessapiv3.azurewebsites.net/api
   */
  public get outdoorAccessPiebaldApiUrl() {
    return 'https://outdooraccessapiv3.azurewebsites.net/api';
  }
}
